import React from "react";
import { Dialog, Box, Typography, IconButton, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ViewListIcon from "@mui/icons-material/ViewList";
import CheckIcon from "@mui/icons-material/Check";
import SettingsIcon from "@mui/icons-material/Settings";
import AnimatedOrb from "../dashboard/TaskGuide/AnimatedOrb";
import SpeechBubble from "../dashboard/TaskGuide/SpeechBubble";
import useUserAPIClient from "../../api/user/hooks/useUserAPIClient";
import { dispatch } from "../../redux/store";
import { setTaskCreationPreference } from "../../redux/actions/user";

const OPTIONS = [
  {
    key: "guided",
    icon: <AutoFixHighIcon sx={{ color: "#813773", fontSize: 32 }} />,
    iconBg: "rgba(129, 55, 115, 0.12)",
    accentColor: "#813773",
    label: "Step-by-step",
    tagline: "Guided walkthrough with Ori",
    features: [
      "One field at a time",
      "Tips & hints along the way",
      "Great for getting started",
    ],
  },
  {
    key: "form",
    icon: <ViewListIcon sx={{ color: "#276580", fontSize: 32 }} />,
    iconBg: "rgba(39, 101, 128, 0.12)",
    accentColor: "#276580",
    label: "Quick Form",
    tagline: "Everything on one page",
    features: [
      "All fields at a glance",
      "Fill in only what you need",
      "Best for experienced users",
    ],
  },
];

/**
 * Dialog shown on first "Create Task" click.
 * Ori the orb asks how the user prefers to create tasks.
 */
const TaskCreationPreferenceDialog = ({ open, onClose, onSelected }) => {
  const { setTaskCreationPreference: savePreference } = useUserAPIClient();

  const handleSelect = async (preference) => {
    dispatch(setTaskCreationPreference(preference));
    savePreference(preference).catch((err) =>
      console.error("Failed to save task creation preference:", err),
    );
    onSelected(preference);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: "task-guide-dialog",
        sx: { borderRadius: "20px", overflow: "hidden", maxHeight: "90vh" },
      }}
    >
      <Box className="task-guide-dialog__header">
        <IconButton
          onClick={onClose}
          className="task-guide-dialog__close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="task-guide-dialog__body scroller">
        <Fade in={open} timeout={300}>
          <Box className="task-guide-dialog__step-container">
            <Box className="task-guide-step">
              <AnimatedOrb size="medium" intensity="idle" />
              <SpeechBubble>
                <Typography variant="body1" sx={{ margin: 0, textAlign: "center" }}>
                  Before we get started — how would you like to create your tasks?
                </Typography>
              </SpeechBubble>

              {/* Side-by-side approach cards — same pattern as ApproachStep */}
              <Box className="task-guide-step__approach-options">
                {OPTIONS.map((opt) => (
                  <Box
                    key={opt.key}
                    className="task-guide-step__approach-card"
                    onClick={() => handleSelect(opt.key)}
                  >
                    {/* Coloured icon circle */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "16px",
                        backgroundColor: opt.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                        flexShrink: 0,
                      }}
                    >
                      {opt.icon}
                    </Box>

                    {/* Title */}
                    <Typography variant="h3" sx={{ mb: 0.5 }}>
                      {opt.label}
                    </Typography>

                    {/* Tagline */}
                    <Typography variant="body2" sx={{ mb: 1.5, fontSize: "0.75rem" }}>
                      {opt.tagline}
                    </Typography>

                    {/* Feature list — left-aligned within the card */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        mt: "auto",
                      }}
                    >
                      {opt.features.map((feature) => (
                        <Box
                          key={feature}
                          sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                        >
                          <CheckIcon
                            sx={{
                              fontSize: 14,
                              color: opt.accentColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#888",
                              lineHeight: 1.35,
                              textAlign: "left",
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      <Box className="task-guide-dialog__footer">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: 1.5,
            py: 0.75,
            borderRadius: "20px",
            border: "1px solid rgba(0,0,0,0.12)",
            backgroundColor: "rgba(0,0,0,0.03)",
          }}
        >
          <SettingsIcon sx={{ fontSize: 24, color: "#999", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: "#777", lineHeight: 1.3 }}>
            You can change this anytime in <strong>Account → Preferences</strong>
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TaskCreationPreferenceDialog;






